import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle2, XCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const C = {
  bg: '#0F0F0F',
  surf: '#161616',
  card: '#1C1C1C',
  bdr: '#2A2A2A',
  accent: '#D97757',
  green: '#4CAF82',
  red: '#E05C5C',
  text: '#F0EDE8',
  muted: '#888',
};

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: ${C.bg}; color: ${C.text}; font-family: 'Inter', sans-serif; -webkit-font-smoothing: antialiased; }
  @keyframes fadeUp { from { opacity:0; transform:translateY(10px) } to { opacity:1; transform:translateY(0) } }
  @keyframes spin   { to { transform: rotate(360deg) } }
`;

export default function VerifyEmailPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying | success | error
  const [message, setMessage] = useState('');
  const hasRunRef = useRef(false);

  useEffect(() => {
    if (hasRunRef.current) return;
    hasRunRef.current = true;
    const verify = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/auth/verify-email/${token}`, {
          method: 'POST',
          credentials: 'include',
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok) {
          setStatus('success');
          setMessage(data.email ? `Email ${data.email} verified successfully.` : 'Email verified successfully.');
        } else {
          setStatus('error');
          setMessage(data.detail || 'Could not verify this link.');
        }
      } catch (err) {
        setStatus('error');
        setMessage('Network error. Please try again later.');
      }
    };
    verify();
  }, [token]);

  return (
    <>
      <style>{styles}</style>
      <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
        <div style={{ width: '100%', maxWidth: 440, animation: 'fadeUp .4s ease' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 32, justifyContent: 'center' }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: C.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700, color: '#fff' }}>C</div>
            <span style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-.5px' }}>
              Content<span style={{ color: C.accent }}>Gap</span>
            </span>
          </div>

          <div style={{ background: C.card, border: `0.5px solid ${C.bdr}`, borderRadius: 12, padding: '32px 28px', textAlign: 'center' }}>
            {status === 'verifying' && (
              <>
                <div style={{ width: 32, height: 32, border: `2px solid ${C.bdr}`, borderTopColor: C.accent, borderRadius: '50%', animation: 'spin .7s linear infinite', margin: '0 auto 16px' }} />
                <h1 style={{ fontSize: 19, fontWeight: 500, marginBottom: 6 }}>Verifying your email...</h1>
                <p style={{ fontSize: 14, color: C.muted, lineHeight: 1.6 }}>Just a moment.</p>
              </>
            )}
            {status === 'success' && (
              <>
                <CheckCircle2 size={48} color={C.green} strokeWidth={1.6} style={{ marginBottom: 14 }} />
                <h1 style={{ fontSize: 21, fontWeight: 500, marginBottom: 8, letterSpacing: '-.3px' }} data-testid="verify-success-heading">Email verified! 🎉</h1>
                <p style={{ fontSize: 14, color: C.muted, lineHeight: 1.7, marginBottom: 22 }}>{message}</p>
                <button
                  onClick={() => navigate('/')}
                  data-testid="continue-to-app-btn"
                  style={{ padding: '11px 24px', background: C.accent, color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer' }}
                >
                  Continue to ContentGap →
                </button>
              </>
            )}
            {status === 'error' && (
              <>
                <XCircle size={48} color={C.red} strokeWidth={1.6} style={{ marginBottom: 14 }} />
                <h1 style={{ fontSize: 21, fontWeight: 500, marginBottom: 8, letterSpacing: '-.3px' }} data-testid="verify-error-heading">Verification failed</h1>
                <p style={{ fontSize: 14, color: C.muted, lineHeight: 1.7, marginBottom: 22 }}>{message}</p>
                <button
                  onClick={() => navigate('/login')}
                  style={{ padding: '11px 24px', background: 'transparent', color: C.text, border: `0.5px solid ${C.bdr}`, borderRadius: 8, fontSize: 14, cursor: 'pointer' }}
                >
                  Back to sign in
                </button>
              </>
            )}
          </div>

          <p style={{ textAlign: 'center', marginTop: 16, fontSize: 13, color: '#444' }}>
            Powered by ContentGap
          </p>
        </div>
      </div>
    </>
  );
}
